import { cn } from "@/utils/cn";
import { MapPin, X } from "lucide-react";
import React, { useEffect } from "react";
import { Button } from "../../rac/button";
import { useAutocomplete } from "./hooks";
import type { AutocompleteItem, AutocompleteProps } from "./types";

declare global {
	interface Window {
		settlinDropdownRef?: HTMLDivElement | null;
	}
}

interface ChipAutocompleteProps extends AutocompleteProps {
	selectedItems?: AutocompleteItem[];
	onItemRemove?: (itemId: string) => void;
	showChips?: boolean;
	chipsPosition?: "above" | "below";
	filterField?: boolean;
	isScrollIntoView?: boolean;
}

export const Autocomplete: React.FC<ChipAutocompleteProps> = ({
	value,
	items,
	placeholder = "Enter text",
	className = "",
	isReady = true,
	isValueSelected = false,
	onInputChange,
	onItemSelect,
	onClear,
	onValueSelectedChange,
	renderItem,
	inputProps = {},
	dropdownProps = {},
	selectedItems = [],
	onItemRemove,
	showChips = false,
	chipsPosition = "above",
	filterField = false,
	isScrollIntoView = true,
	autoFocus = false,
}) => {
	const {
		inputRef,
		dropdownRef,
		isDropdownOpen,
		handleInputChangeWithDropdown,
		handleItemSelect,
		handleClearInput,
		handleInputFocus,
	} = useAutocomplete(
		value,
		items,
		isReady,
		isValueSelected,
		onInputChange,
		onItemSelect,
		onClear,
		onValueSelectedChange,
	);

	// Enhanced item selection that clears input only in filterField mode
	const handleEnhancedItemSelect = (itemId: string) => {
		handleItemSelect(itemId);
		// Only clear the input after selection in filterField mode
		if (filterField) {
			handleClearInput();
		}
	};

	// Default item renderer
	const defaultRenderItem = (item: AutocompleteItem) => (
		<div
			key={item.id}
			onClick={() => {
				if (!item.isPlaceholder) {
					handleEnhancedItemSelect(item.id);
				}
			}}
			onMouseDown={_e => {
				_e.preventDefault();
			}}
			className={cn(
				"px-4 py-3 cursor-pointer",
				item.isPlaceholder ? "text-muted-foreground cursor-default" : "hover:bg-accent hover:text-accent-foreground",
			)}
		>
			<div className="flex flex-col">
				<div className="text-sm font-medium">{item.text}</div>
				{item.secondaryText && <div className="text-xs text-muted-foreground mt-0.5">{item.secondaryText}</div>}
			</div>
		</div>
	);

	const itemRenderer = renderItem || defaultRenderItem;

	// Extract onChange and autoFocus from inputProps to avoid overriding internal handler
	// autoFocus from inputProps will be ignored in favor of the autoFocus prop
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const {
		onChange: inputPropsOnChange,
		className: inputPropsClassName = "",
		autoFocus: _autoFocusFromInputProps,
		...restInputProps
	} = inputProps;

	useEffect(() => {
		if (isDropdownOpen && dropdownRef.current) {
			window.settlinDropdownRef = dropdownRef.current;
		} else if (!isDropdownOpen) {
			window.settlinDropdownRef = null;
		}
	}, [isDropdownOpen, dropdownRef]);

	// Improved mobile scroll handling with forced reflow optimization
	const scrollInputIntoView = (element: HTMLElement) => {
		if (!isScrollIntoView) return;

		// Method 1: Use setTimeout to wait for keyboard animation
		setTimeout(() => {
			// Use requestAnimationFrame to batch DOM reads and prevent forced reflows
			requestAnimationFrame(() => {
				// Method 2: Calculate offset to account for virtual keyboard
				const rect = element.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				const elementTop = rect.top;
				const elementHeight = rect.height;

				// If element is in bottom half of screen, scroll it to top third
				if (elementTop > viewportHeight * 0.5) {
					const scrollOffset = elementTop - viewportHeight * 0.33;
					window.scrollBy({
						top: scrollOffset,
						behavior: "smooth",
					});
				} else {
					// Fallback to standard scrollIntoView
					element.scrollIntoView({
						behavior: "smooth",
						block: "center", // Changed from "start" to "center"
						inline: "nearest",
					});
				}
			});
		}, 150); // Wait for keyboard animation

		// Method 3: Additional scroll after keyboard is fully shown
		setTimeout(() => {
			const rect = element.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			// If element is still hidden behind keyboard, scroll more
			if (rect.bottom > viewportHeight * 0.75) {
				element.scrollIntoView({
					behavior: "smooth",
					block: "start",
					inline: "nearest",
				});
			}
		}, 500);
	};

	const handleFocus = (event: FocusEvent) => {
		const target = event.target;
		if (
			target instanceof HTMLInputElement ||
			target instanceof HTMLTextAreaElement ||
			target instanceof HTMLSelectElement
		) {
			// Method 4: Detect mobile browsers
			const isMobile =
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
				(navigator.maxTouchPoints && navigator.maxTouchPoints > 2);

			if (isMobile) {
				scrollInputIntoView(target);
			} else if (isScrollIntoView) {
				// Desktop fallback - use "center" to avoid scrolling behind fixed nav bars
				target.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		}
	};

	useEffect(() => {
		const element = inputRef.current;
		if (element) {
			element.addEventListener("focus", handleFocus);
			return () => element.removeEventListener("focus", handleFocus);
		}
	}, [inputRef]);

	// Additional mobile keyboard handling
	useEffect(() => {
		const handleVisualViewportChange = () => {
			if (document.activeElement === inputRef.current && window.visualViewport) {
				// Scroll when visual viewport changes (keyboard opens/closes)
				const element = inputRef.current;
				if (element) {
					setTimeout(() => {
						scrollInputIntoView(element);
					}, 100);
				}
			}
		};

		// Use Visual Viewport API if available (modern browsers)
		if (window.visualViewport) {
			window.visualViewport.addEventListener("resize", handleVisualViewportChange);
			return () => {
				window.visualViewport?.removeEventListener("resize", handleVisualViewportChange);
			};
		}
	}, [inputRef]);

	// Chips component to avoid duplication
	const ChipsSection =
		showChips && selectedItems.length > 0 ? (
			<div className={cn("flex flex-wrap gap-2", chipsPosition === "above" ? "mb-2" : "mt-2")}>
				{selectedItems.map(item => (
					<div
						key={item.id}
						className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gray-300 bg-white text-xs"
					>
						<MapPin className="w-3 h-3 text-primary fill-primary-100" aria-hidden="true" />
						<span className="text-black font-normal">{item.text}</span>
						{onItemRemove && (
							<button
								type="button"
								aria-label={`Remove ${item.text}`}
								onClick={() => onItemRemove(item.id)}
								className="ml-1 hover:bg-gray-100 rounded-full p-0.5"
							>
								<X className="w-3 h-3 text-red-400" aria-hidden="true" />
							</button>
						)}
					</div>
				))}
			</div>
		) : null;

	return (
		<div className={cn("relative w-full h-full", className)}>
			{/* Chips above input */}
			{chipsPosition === "above" && ChipsSection}

			<input
				ref={inputRef}
				type="text"
				placeholder={placeholder}
				value={value}
				onChange={e => {
					handleInputChangeWithDropdown(e.target.value);
					// Call the external onChange if provided
					if (inputPropsOnChange) {
						inputPropsOnChange(e);
					}
				}}
				onFocus={handleInputFocus}
				autoFocus={autoFocus}
				className={cn(
					"w-full h-full px-1 py-2 pr-10 bg-background text-foreground border-0 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0",
					filterField && "mt-2 rounded",
					!filterField && "rounded-none",
					inputPropsClassName,
				)}
				{...restInputProps}
			/>
			{value && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="absolute right-2 top-1/2 -translate-y-1/2 size-6 p-1"
					onPress={() => handleClearInput()}
					aria-label="Clear input"
				>
					<X className="size-4 opacity-50" aria-hidden="true" />
				</Button>
			)}

			{/* Chips below input */}
			{chipsPosition === "below" && ChipsSection}

			{/* Dropdown positioned absolutely relative to this container */}
			{isDropdownOpen && isReady && items.length > 0 && (
				<div
					ref={dropdownRef}
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
					onMouseDown={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
					className={cn(
						"absolute z-[999999] bg-white border border-gray-200 shadow-lg rounded-md overflow-y-auto py-2",
						dropdownProps.className,
					)}
					style={{
						top: "100%",
						left: "0",
						width: "100%",
						maxWidth: "90dvw",
						position: "absolute",
						zIndex: 999999,
						maxHeight: "200px",
						overflowY: "scroll",
						...dropdownProps.style,
					}}
					{...dropdownProps}
				>
					{items.map(item => itemRenderer(item))}
				</div>
			)}
		</div>
	);
};

// Export types and hooks for external use
export * from "./hooks";
export * from "./types";
